<template>
  <navbar-component :currentUserRoles="currentUserStore.roles" :contactId="currentUserStore.contactId"
    :currentUserPreferences="preferences" :auth="auth" :key="user" @logout="logout" />
  <ImpersonationStatusComponent />

  <router-view :currentUserRoles="currentUserStore.roles" :contactId="currentUserStore.contactId"
    :currentUserPreferences="preferences" :county="countyStore.county" />

  <footer v-if="$route.name != 'maps'" class="row row-cols-1 row-cols-sm-2 row-cols-md-5 mt-5 pt-1 border-top">
    <div class="col col-md-9">
      <a href="/" class="d-flex align-items-center mb-3 link-dark text-decoration-none">
        <!-- <img class="footer-logo"  alt="GCDP Seal" :src="footerLogo"> -->
        <span class="footer-logo"></span>
      </a>
      <p class="text-muted">© 2024</p>
    </div>

    <div class="col col-md-3" style="scale:75%">
      <UserRoleProvider :auth="auth" style="scale:75%" @user-roles-updated="handleUserRolesUpdated" />
      <br />
      <RouterLink to="/privacy">Privacy Policy</RouterLink>
      <br />
      <a title="Contact ID"><b>CID</b>: {{ currentUserStore.contactId || "Unknown Contact" }}</a>
      <br />
      <b>UID</b>: {{ user?.uid || "Unknown User" }}
    </div>
  </footer>
  <template v-else>
    <!-- No footer in map view, but we still need to know user roles! -->
    <UserRoleProvider :auth="auth" style="scale:75%; display: none" @user-roles-updated="handleUserRolesUpdated" />
  </template>
</template>

<script>
import { getAuth, signOut, onAuthStateChanged, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth'
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useRouter } from 'vue-router'
import UserRoleProvider from '@/components/UserRoleProvider.vue';
import NavbarComponent from '@/components/NavbarComponent.vue';
import ImpersonationStatusComponent from '@/common/components/admin/ImpersonationStatusComponent.vue';
import { ensureAppCheckToken } from '@/lib/localFirebase';
import { initializePreferences, preferences } from '@/components/userPreferences';
import { mapStores } from 'pinia'
import { useCountyStore } from '@/common/stores/countyStore';
import { useCurrentUserStore } from '@/common/stores/currentUserStore';

export default {
  name: 'App',
  components: {
    NavbarComponent,
    UserRoleProvider,
    ImpersonationStatusComponent
  },
  data() {
    return {
      auth: getAuth(), // Get auth instance here
      router: useRouter(),
      isLoggedIn: false,
      preferences: preferences,
      user: null,
    };
  },
  computed: {
    ...mapStores(useCountyStore, useCurrentUserStore),
  },
  async mounted() {
    const href = window.location.href;
    if (isSignInWithEmailLink(this.auth, href)) {
      // If the user is signing in with an email link, we need to complete the sign-in.
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        const urlParams = new URLSearchParams(window.location.search);
        email = urlParams.get('email');
      }

      if (!email) {
        email = window.prompt("Please provide your email for sign-in:");
      }
      if (email) {
        console.log("Signing in with email link for:", email);
        ensureAppCheckToken().then(() => signInWithEmailLink(this.auth, email, href)).then(() => {
          window.localStorage.removeItem('emailForSignIn');
          window.history.replaceState({}, '', this.$router.currentRoute.value.query.referrer || href);
        }).catch((error) => {
          console.error("Error signing in with email link:", error);
        });
      }
    }

    // runs after firebase is initialized
    onAuthStateChanged(this.auth, async (u) => {
      if (isSignInWithEmailLink(this.auth, window.location.href)) {
        console.log("User auth state changed, but we're signing in with email link. Skipping redirect to /login.", u);
        return
      }
      this.user = u;
      if (u) {
        this.isLoggedIn = true // if we have a user
        this.createRolesDocument(u)
      } else {
        this.isLoggedIn = false // if we do not
        // Sometimes the currentRoute is straight up wrong.
        await this.$router.isReady()
        if (!this.$router.currentRoute.value.meta.public) {
          // Redirect unauthenticated users to /login with.
          console.log(`Route ${this.$router.currentRoute.value.fullPath} is not public, redirecting...`)
          this.$router.push({ path: '/login', query: { referrer: this.$router.currentRoute.value.fullPath } });
        }
      }
    })

    initializePreferences();
    useCountyStore().initializeData();
    useCurrentUserStore().initialize();
  },
  methods: {
    async createIfMissing(collection, item, data) {
      try {
        const userDocRef = doc(window.db, collection, item);
        const userDocSnapshot = await getDoc(userDocRef);

        if (!userDocSnapshot.exists()) {
          await setDoc(userDocRef, data);
        }
      } catch (error) {
        console.error("Error creating " + collection + " document: ", error);
      }
    },
    async handleUserRolesUpdated() {
      this.$forceUpdate();
    },
    // Function to create user document
    async createRolesDocument(user) {
      // Don't bother. I made this secure.
      this.createIfMissing("userRoles", user.email, { roles: [], userId: user.uid });
    },
    logout() {
      // Seems like UserRoleProvider should do this, but it doesn't work well.
      this.handleUserRolesUpdated([]);

      signOut(this.auth).then(() => {
        this.router.push('/login')
      })
    },
    setCounty(county) {
      this.countyStore.setCounty(county);
    },
  }

}
</script>
